import { createSessionEngagement } from './session-engagement.js';
import { observeClickFriction } from './click-friction.js';
import { clickFrictionEventProps } from '../shared/click-friction-schema.js';
import { errorReport, serializeErrorReport } from '../shared/error-report.js';
import { createStartupPerformance } from './startup-performance.js';
import { queueAnalyticsEvent, flushAnalyticsEvents } from './analytics-delivery.js';
import { isPlausibleEventInteractive } from '../shared/plausible-events.js';
import type { SchemaInput, SchemaRecord } from '../shared/schema-input.js';
import { isBoolean, isFunction, isObject, isString, isSchemaRecord } from '../shared/runtime-values.js';
import { COMMON_PROP_ALLOWLIST } from '../shared/analytics-schema.js';
import type {
  AnalyticsEventName,
  AnalyticsEventWithProp,
  AnalyticsTrack,
  EventPropAllowlist,
  PropRule,
} from '../shared/analytics-schema.js';
import { analyticsLanguageContext, analyticsPageContext } from './analytics-context.js';
import {
  getOrCreateAnalyticsVisitorId,
  isAnalyticsOptedOut,
} from './analytics-identity.js';
import { isKnownNonAppErrorStack, isKnownNonAppRuntimeError } from './runtime-error-filter.js';
import { searchAnalyticsProps } from './search-analytics-props.js';
import { setAutocompleteTelemetryReporter } from './autocomplete-telemetry.js';
import { getCanvasDrawFailure } from './canvas-error-diagnostics.js';
import { customerAnalyticsEnvelope, refreshCustomerAnalytics } from './customer-analytics-context.js';

interface AnalyticsPropertyBag {
  [name: string]: string;
}
import {
  subscribeRuntimeErrorSource,
  writeRuntimeSourceConsoleError,
  type RuntimeErrorSourceEvent,
} from './runtime-error-source.js';

interface AnalyticsOptions<Allowlist extends EventPropAllowlist> {
  app: string;
  endpoint: string;
  appVersion: string;
  eventPropAllowlist: Allowlist;
  firstActionEventByChannel: Partial<Record<AnalyticsChannel, AnalyticsEventWithProp<Allowlist, 'session_mode'>>>;
  sessionActionEventByChannel: Partial<Record<AnalyticsChannel, AnalyticsEventWithProp<Allowlist, 'session_mode'>>>;
  engagementMilestones: ReadonlyArray<{ ms: number; event: AnalyticsEventWithProp<Allowlist, 'session_mode'> }>;
  slowReadyEvents: Partial<Record<SlowReadyChannel, AnalyticsEventName<Allowlist>>>;
  runtimeErrorEvent: AnalyticsEventName<Allowlist>;
  consoleErrorEvent: AnalyticsEventName<Allowlist>;
  consoleWarnPatterns?: Array<{
    pattern: RegExp | string;
    event: AnalyticsEventName<Allowlist>;
  }>;
}

type AnalyticsChannel = 'content_link' | 'create_map' | 'draw' | 'footer_link' | 'locate' | 'my_maps' | 'navigate' | 'navigation' | 'no_action' | 'no_interaction' | 'region' | 'save' | 'search' | 'share' | 'style' | 'tutorial';
type SlowReadyChannel = 'slow3s' | 'slow5s';

interface GeoDetail {
  subtype?: string;
  error?: Error | null;
  response?: { status?: number; statusText?: string; url?: string } | null;
}

export function createAnalyticsClient<const Allowlist extends EventPropAllowlist>({
  app,
  endpoint,
  appVersion,
  eventPropAllowlist,
  firstActionEventByChannel,
  sessionActionEventByChannel,
  engagementMilestones,
  slowReadyEvents,
  runtimeErrorEvent,
  consoleErrorEvent,
  consoleWarnPatterns = []
}: AnalyticsOptions<Allowlist>) {
  void refreshCustomerAnalytics();
  const refreshCustomerContext = () => { void refreshCustomerAnalytics(); };
  window.addEventListener('focus', refreshCustomerContext);

  const landingPage = window.location.pathname.toLowerCase();
  const queryParams = new URLSearchParams(window.location.search);
  const utmSource = queryParams.get('utm_source') ?? '';
  const utmMedium = queryParams.get('utm_medium') ?? '';
  const utmCampaign = queryParams.get('utm_campaign') ?? '';

  function readReferrerHost(): string {
    const ref = document.referrer;
    if (!ref) return 'direct';
    try {
      const host = new URL(ref).hostname.toLowerCase();
      if (!host) return 'unknown';
      if (host === window.location.hostname.toLowerCase()) return 'internal';
      return host.slice(0, 120);
    } catch {
      return 'unknown';
    }
  }

  const referrerHost = readReferrerHost();

  function trafficSourceFrom(): string {
    const source = utmSource.toLowerCase();
    const medium = utmMedium.toLowerCase();
    if (medium === 'email' || source.includes('email') || source.includes('newsletter')) return 'email';
    if (medium === 'cpc' || medium === 'ppc' || medium === 'paid' || medium === 'paid_search') return 'paid';
    if (['chatgpt', 'chatgpt.com', 'chat.openai.com'].includes(source)
      || ['chatgpt.com', 'www.chatgpt.com', 'chat.openai.com'].includes(referrerHost)) return 'ai_search';
    if (utmSource || utmMedium || utmCampaign) return 'campaign';
    if (referrerHost === 'direct') return 'direct';
    if (referrerHost === 'internal') return 'internal';
    if (/(\.|^)(google|bing|duckduckgo|search\.yahoo|yandex|baidu|ecosia|brave)\./.test(referrerHost)) return 'organic_search';
    if (/(\.|^)(chatgpt|perplexity|claude|gemini|copilot)\./.test(referrerHost)) return 'ai_search';
    if (/(\.|^)(facebook|instagram|linkedin|twitter|x|reddit|pinterest|youtube|tiktok)\./.test(referrerHost)) return 'social';
    return 'referral';
  }

  function searchIntentFrom(path: string): string {
    if (/\/printable-custom-map\/?$/.test(path)) return 'printable_export';
    if (/\/(us|usa|interactive-us)-map\/?$/.test(path)) return 'us_map';
    if (/\/world-map\/?$/.test(path)) return 'world_map';
    if (/\/(africa|asia|australia|canada|europe|uk)-map\/?$/.test(path)) return 'region_map';
    if (/\/(route-planner|plot-a-route)\/?$/.test(path)) return 'route_planner';
    if (/\/distance-between-two-addresses\/?$/.test(path)) return 'distance';
    if (/\/(map-radius-tool|draw-circle-on-map)\/?$/.test(path)) return 'radius';
    if (/\/embed-map\/?$/.test(path)) return 'embed_map';
    if (/\/map-marker\/?$/.test(path)) return 'marker';
    if (/\/mapping-tool\/?$/.test(path)) return 'mapping_tool';
    if (path === '/' || /^\/[a-z]{2}\/?$/.test(path)) return 'draw_map';
    return 'unknown';
  }

  const trafficSource = trafficSourceFrom();
  const searchIntent = searchIntentFrom(landingPage);

  const initialPageContext = analyticsPageContext();
  const sessionAttributionProps = {
    landing_page: landingPage,
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    referrer_host: referrerHost,
    traffic_source: trafficSource,
    search_intent: searchIntent,
    ...analyticsLanguageContext(),
    form_factor: initialPageContext.form_factor,
    input_capabilities: initialPageContext.input_capabilities,
    display_mode: initialPageContext.display_mode
  };
  const sessionId = initialPageContext.session_id;
  const visitorId = getOrCreateAnalyticsVisitorId();

  let sessionMode = 'start_blank';
  const engagement = createSessionEngagement({
    firstAction(channel) {
      // SAFETY: Only configured session channels produce an event.
      const event = firstActionEventByChannel[channel as AnalyticsChannel];
      if (!event) return false;
      trackWithSessionMode(event); return true;
    },
    noAction: () => { const event = firstActionEventByChannel.no_action; if (event) trackWithSessionMode(event); },
    noInteraction: () => { const event = firstActionEventByChannel.no_interaction; if (event) trackWithSessionMode(event); },
    milestone: event => trackWithSessionMode(event),
    milestones: engagementMilestones,
  });
  const trackedRuntimeErrors = new Set<string>();
  let hasTrackedAppReady = false;
  const startupPerformance = createStartupPerformance((event, props) => trackUnchecked(event, props));
  let lastConsoleErrorMessage = '';
  let lastConsoleErrorAt = 0;
  let lastIgnoredNonAppConsoleErrorAt = 0;
  const trackedSessionActionChannels = new Set<string>();
  const trackedAutocompleteFailureBuckets = new Set<string>();
  const flowTimers = new Map<string, number>();
  const rageClickHistory = new Map<string, number[]>();
  const trackedRageTargets = new Set<string>();

  function isAllowedAnalyticsValue(allowlist: PropRule, value: SchemaInput): boolean {
    if (!isString(value)) return false;
    if (allowlist instanceof Set) return allowlist.has(value);
    if (allowlist instanceof RegExp) return allowlist.test(value);
    return false;
  }

  function truncateString(value: SchemaInput, maxLength: number): string {
    const text = String(value);
    if (text.length <= maxLength) return text;
    const suffix = '...[truncated]';
    if (maxLength <= suffix.length) return text.slice(0, maxLength);
    return `${text.slice(0, maxLength - suffix.length)}${suffix}`;
  }

  function sanitizeSourcePath(source: string): string {
    if (!isString(source) || source.length === 0) return 'unknown';
    try {
      const parsed = new URL(source, window.location.href);
      return truncateString(`${parsed.origin}${parsed.pathname}`, 300);
    } catch {
      return truncateString(source, 300);
    }
  }

  function currentPageContext() {
    return {
      page_url: sanitizeSourcePath(window.location.href),
      landing_page: landingPage
    };
  }

  function shouldIgnoreRuntimeErrorMessage(message: string): boolean {
    return isKnownNonAppRuntimeError(message);
  }

  function readErrorLikeDetails(values: unknown[]) {
    for (const value of values) {
      if (!(value instanceof Error)) continue;
      const canvasFailure = getCanvasDrawFailure(value);
      return {
        ...errorReport(value, 'Unknown error'),
        error_name: truncateString(value.name || 'Error', 80),
        error_message: truncateString(value.message || 'unknown', 500),
        canvas_draw: canvasFailure ? { ...canvasFailure, stack: undefined } : undefined,
        stack_head: canvasFailure?.stack || truncateString(
          isString(value.stack)
            ? value.stack.split('\n').slice(0, 12).join('\n')
            : 'unknown',
          2200
        )
      };
    }
    return { stack_head: undefined };
  }

  function serializeErrorBlob(blob: SchemaInput): string {
    return serializeErrorReport(isSchemaRecord(blob) ? blob : { message: String(blob) });
  }

  function toConsoleMessage(args: unknown[]): string {
    const parts = args
      .map(a => (isString(a) ? a.trim() : a instanceof Error ? `${a.name}: ${a.message}` : null))
      .filter(Boolean);
    if (parts.length > 0) return truncateString(parts.join(' '), 500);
    return 'console.error';
  }

  function shouldTrackConsoleError(args: unknown[]): boolean {
    if (!args.length) return true;
    const message = toConsoleMessage(args);
    if (isKnownNonAppRuntimeError(message)) {
      lastIgnoredNonAppConsoleErrorAt = Date.now();
      return false;
    }
    const head = isString(args[0]) ? args[0] : '';
    if (head === '[App Error]') return false;
    if (head.startsWith('Analytics request failed:')) return false;
    if (head.startsWith('Analytics error:')) return false;
    return true;
  }

  function trackPlausible(event: string, props: Record<string, string> = {}): void {
    if (isAnalyticsOptedOut()) return;
    if (!isFunction(window.plausible)) return;

    try {
      const plausibleProps = { ...props };
      delete plausibleProps.session_id;
      delete plausibleProps.visitor_id;
      const options = { interactive: isPlausibleEventInteractive(event, plausibleProps) };
      if (Object.keys(plausibleProps).length > 0) {
        window.plausible(event, { ...options, props: plausibleProps });
        return;
      }
      window.plausible(event, options);
    } catch (err) {
      writeRuntimeSourceConsoleError('Plausible analytics error:', err);
    }
  }

  const frictionRules: EventPropAllowlist = clickFrictionEventProps(COMMON_PROP_ALLOWLIST);
  function trackUnchecked(event: string, props: Record<string, string> = {}): void {
    if (isAnalyticsOptedOut()) return;
    const propAllowlist = eventPropAllowlist[event] ?? frictionRules[event] ?? COMMON_PROP_ALLOWLIST;
    const pageContext = analyticsPageContext();

    const mergedProps: AnalyticsPropertyBag = {
      traffic_source: trafficSource,
      search_intent: searchIntent,
      session_id: sessionId,
      app,
      device_class: pageContext.device_class,
      os_family: pageContext.os_family,
      app_version: appVersion,
      session_mode: sessionMode,
      ...props
    };
    if (visitorId) mergedProps.visitor_id = visitorId;

    const cleanProps: Record<string, string> = {};
    for (const [key, value] of Object.entries(mergedProps)) {
      const allowlist = propAllowlist[key];
      if (!allowlist) continue;
      if (!isAllowedAnalyticsValue(allowlist, value)) continue;
      cleanProps[key] = value;
    }

    if (event !== 'autocomplete_request_finished' && event !== 'app_ready_performance' && event !== 'startup_phase_performance') trackPlausible(event, cleanProps);

    queueAnalyticsEvent(endpoint, { event, props: cleanProps, ...customerAnalyticsEnvelope() });
  }

  // SAFETY: This adapter forwards events through the same allowlist-backed analytics contract.
  const track = trackUnchecked as AnalyticsTrack<Allowlist>;
  setAutocompleteTelemetryReporter((props) => trackUnchecked('autocomplete_request_finished', props));

  function trackWithSessionMode(event: AnalyticsEventWithProp<Allowlist, 'session_mode'>): void {
    trackUnchecked(event, { session_mode: sessionMode });
  }

  function startFlow(flow: string, timeoutEvent: string, ms = 15000, timeoutProps?: () => Record<string, string>): void {
    const existing = flowTimers.get(flow);
    if (existing !== undefined) window.clearTimeout(existing);
    const timerId = window.setTimeout(() => {
      flowTimers.delete(flow);
      trackUnchecked(timeoutEvent, timeoutProps?.());
    }, ms);
    flowTimers.set(flow, timerId);
  }

  function endFlow(flow: string): void {
    const existing = flowTimers.get(flow);
    if (existing === undefined) return;
    window.clearTimeout(existing);
    flowTimers.delete(flow);
  }

  const clickFriction = observeClickFriction(trackUnchecked);

  function trackRageClick(target: string, event = 'rage_click_detected'): void {
    if (clickFriction.isHandlingClick()) return;
    const now = Date.now();
    const recent = (rageClickHistory.get(target) ?? []).filter((timestamp) => now - timestamp <= 2000);
    recent.push(now);
    rageClickHistory.set(target, recent);
    if (recent.length < 3 || trackedRageTargets.has(target)) return;
    trackedRageTargets.add(target);
    trackUnchecked(event, { target });
  }

  function markAnyInteraction(): void { engagement.interact(); }

  function markMeaningfulAction(channel: string): void { engagement.act(channel); }

  function trackSessionActionOnce(channel: string): void {
    // SAFETY: The registered event name and listener contract define this concrete event type.
    const event = sessionActionEventByChannel[channel as AnalyticsChannel];
    if (!event || trackedSessionActionChannels.has(channel)) return;
    trackedSessionActionChannels.add(channel);
    trackWithSessionMode(event);
  }

  function startNoActionTimer(): void { engagement.startInactivity(); }

  function getOnlineState(): string {
    if (!isBoolean(navigator.onLine)) return 'unknown';
    return navigator.onLine ? 'online' : 'offline';
  }

  function buildGeoErrorBlob(kind: string, query: string, extra: Record<string, string> = {}, errorLike: SchemaInput = null): string {
    return serializeErrorBlob({
      kind,
      ...currentPageContext(),
      online_state: getOnlineState(),
      query_length: String(query.trim().length),
      ...extra,
      ...readErrorLikeDetails(errorLike ? [errorLike] : [])
    });
  }

  function readGeocoderFailureContext(cause: unknown) {
    if (!cause || !isObject(cause)) return {};
    // SAFETY: The adjacent object and field checks validate this boundary value before it is accepted.
    const record = cause as SchemaRecord;
    const context: Record<string, string> = {};
    if (isString(record.geocoderProvider)) context.provider = truncateString(record.geocoderProvider, 40);
    if (isString(record.geocoderFallbackFrom)) context.fallback_from = truncateString(record.geocoderFallbackFrom, 40);
    if (isString(record.geocoderFallbackTrigger)) context.fallback_trigger = truncateString(record.geocoderFallbackTrigger, 80);
    return context;
  }

  function readErrorResponse(cause: unknown): { status?: number; statusText?: string; url?: string } | null {
    if (!cause || !isObject(cause)) return null;
    // SAFETY: The adjacent object and field checks validate this boundary value before it is accepted.
    const response = (cause as SchemaRecord).response;
    if (!response || !isObject(response)) return null;
    // SAFETY: The owned application template defines this element and its concrete DOM type.
    return response as { status?: number; statusText?: string; url?: string };
  }

  function trackGeoFailure(baseEvent: string, query: string, detail: GeoDetail = {}): void {
    const { subtype: explicitSubtype, error = null, response = null } = detail;
    const effectiveResponse = response ?? readErrorResponse(error);
    const subtype = explicitSubtype ?? (effectiveResponse ? 'http' : 'network');
    const eventName = subtype === 'http' ? `${baseEvent}_http` : subtype === 'parse' ? `${baseEvent}_parse` : `${baseEvent}_network`;
    const failureContext = readGeocoderFailureContext(error);
    if (eventName.endsWith('autocomplete_failed_network')) {
      const bucket = `${eventName}:${failureContext.provider ?? 'unknown'}`;
      if (trackedAutocompleteFailureBuckets.has(bucket)) return;
      trackedAutocompleteFailureBuckets.add(bucket);
    }
    const blob = buildGeoErrorBlob(
      eventName,
      query,
      {
        ...failureContext,
        http_status: effectiveResponse ? String(effectiveResponse.status) : 'unknown',
        status_text: effectiveResponse ? truncateString(effectiveResponse.statusText || 'unknown', 120) : 'unknown',
        response_url: effectiveResponse ? sanitizeSourcePath(effectiveResponse.url || '') : 'unknown'
      },
      error
    );

    trackUnchecked(eventName, { ...searchAnalyticsProps(query, error), error_blob: blob });
  }

  function startEngagementTracking(): void { engagement.start(document.visibilityState === 'visible'); }

  function trackAppReadyIfSlow(readyMs = performance.now()): void {
    if (hasTrackedAppReady || !Number.isFinite(readyMs) || readyMs < 0 || readyMs > 9999999) return;
    hasTrackedAppReady = true;
    trackUnchecked('app_ready_performance', { duration_ms: String(Math.round(readyMs)), ...startupPerformance.checkpoint() });
    if (readyMs >= 5000) {
      if (slowReadyEvents.slow5s) trackUnchecked(slowReadyEvents.slow5s);
    } else if (readyMs >= 3000) {
      if (slowReadyEvents.slow3s) trackUnchecked(slowReadyEvents.slow3s);
    }
  }

  function trackRuntimeErrorOnce(errorBlob: string | undefined): void {
    const key = errorBlob ?? 'unknown';
    if (trackedRuntimeErrors.has(key) || trackedRuntimeErrors.size >= 10) return;
    trackedRuntimeErrors.add(key);
    if (isString(errorBlob)) {
      trackUnchecked(runtimeErrorEvent, { error_blob: errorBlob });
      return;
    }
    trackUnchecked(runtimeErrorEvent);
  }

  function handleWindowError(event: ErrorEvent): void {
    const message = truncateString(event.message || 'window error', 500);
    const source = sanitizeSourcePath(event.filename);
    if (isKnownNonAppRuntimeError(message, navigator.userAgent, source)) return;
    const errorBlob = serializeErrorBlob({
      kind: 'window_error',
      message,
      source,
      line: Number.isFinite(event.lineno) ? String(event.lineno) : 'unknown',
      column: Number.isFinite(event.colno) ? String(event.colno) : 'unknown',
      ...currentPageContext(),
      ...readErrorLikeDetails([event.error])
    });
    trackRuntimeErrorOnce(errorBlob);
  }

  function handleUnhandledRejection(event: PromiseRejectionEvent): void {
    const reason = event.reason;
    const message = reason instanceof Error
      ? reason.message || 'unhandled rejection'
      : isString(reason)
        ? reason
        : 'unhandled rejection';
    if (shouldIgnoreRuntimeErrorMessage(message)) return;
    if (message === 'unhandled rejection' && Date.now() - lastIgnoredNonAppConsoleErrorAt <= 1000) return;
    const errorDetails = readErrorLikeDetails([reason]);
    if (message === 'unhandled rejection' && !errorDetails.stack_head) return;
    if (isKnownNonAppErrorStack(errorDetails.stack_head)) return;
    const errorBlob = serializeErrorBlob({
      kind: 'unhandled_rejection',
      message: truncateString(message, 500),
      last_console_error: lastConsoleErrorAt && Date.now() - lastConsoleErrorAt <= 1000
        ? truncateString(lastConsoleErrorMessage, 500)
        : 'none',
      ...currentPageContext(),
      ...errorDetails
    });
    trackRuntimeErrorOnce(errorBlob);
  }

  function handleConsoleError(args: unknown[]): void {
    if (!shouldTrackConsoleError(args)) return;

    const errorDetails = readErrorLikeDetails(args);
    if (isKnownNonAppErrorStack(errorDetails.stack_head)) return;

    const errorBlob = serializeErrorBlob({
      kind: 'console_error',
      message: toConsoleMessage(args),
      ...currentPageContext(),
      ...errorDetails
    });

    lastConsoleErrorMessage = toConsoleMessage(args);
    lastConsoleErrorAt = Date.now();
    trackUnchecked(consoleErrorEvent, { error_blob: errorBlob });
  }

  function handleConsoleWarn(args: unknown[]): void {
    const head = isString(args[0]) ? args[0] : '';
    for (const { pattern, event } of consoleWarnPatterns) {
      const matched = isString(pattern) ? head.includes(pattern) : pattern.test(head);
      if (matched) { trackUnchecked(event); return; }
    }
  }

  const unsubscribeRuntimeErrors = subscribeRuntimeErrorSource((event: RuntimeErrorSourceEvent) => {
    if (event.type === 'window_error') handleWindowError(event.event);
    else if (event.type === 'unhandled_rejection') handleUnhandledRejection(event.event);
    else if (event.type === 'console_error') handleConsoleError(event.args);
    else handleConsoleWarn(event.args);
  });

  function handleVisibilityChange(): void { engagement.visible(document.visibilityState === 'visible'); }

  function handlePageHide(): void {
    engagement.hide();
    flushAnalyticsEvents(endpoint);
  }

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', handlePageHide);

  function dispose(): void {
    clickFriction.dispose();
    startupPerformance.dispose();
    flushAnalyticsEvents(endpoint);
    window.removeEventListener('focus', refreshCustomerContext);
    unsubscribeRuntimeErrors();
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('pagehide', handlePageHide);
    engagement.dispose();
    for (const timerId of flowTimers.values()) window.clearTimeout(timerId);
    flowTimers.clear();
  }

  return {
    landingPage,
    sessionAttributionProps,
    searchIntent,
    trafficSource,
    referrerHost,
    utmSource,
    utmMedium,
    utmCampaign,
    appLocale: sessionAttributionProps.app_locale,
    browserLanguage: sessionAttributionProps.browser_language,
    browserLanguagePrimary: sessionAttributionProps.browser_language_primary,
    dispose,
    markAnyInteraction,
    markMeaningfulAction,
    endFlow,
    setSessionMode(mode: string) {
      sessionMode = mode;
    },
    searchAnalyticsProps,
    startEngagementTracking,
    startFlow,
    startNoActionTimer,
    track,
    trackAppReadyIfSlow,
    startStartupPhase: startupPerformance.startPhase,
    measureStartupPromise: startupPerformance.measurePromise,
    trackGeoFailure,
    trackRageClick,
    trackSessionActionOnce,
    trackWithSessionMode
  };
}
